import { Box, Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constant';
import { noop } from 'shell/utils/noop';
import ListYesNo from 'shell/components/shared/list-yes-no';
import { Delete, Visibility } from '@mui/icons-material';
import AccordionPerson from './accordion-person';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { TypeRegister } from 'practice/constants/type-register';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { useFormikContext } from 'formik';
import {
  AccordionInterestedPeopleVM,
  PracticeDetailVM,
  BodyInterestedPeopleVM,
} from 'practice/view-models/practice-detail';
import { TypePractice } from 'practice/constants/type-practice';
import { ID_STATES_PRACTICE } from 'practice/constants/id-states-practice';
import { StepButton } from 'shell/components/shared/step-button';
import { InterestedPeopleData } from 'shell/models/practice';
import { initialValuesNota } from 'practice/components/shared/n010-modal';
import ErrorModal from 'shell/components/shared/modal-error';
import { HttpResponse } from 'shell/models/http-response';

interface FormProps {
  disabled?: boolean;
}

const FormComponent = ({ disabled }: FormProps): React.ReactElement => {
  const params = useParams();

  const typeRegister = useMemo(() => {
    return params.tipoRegistro;
  }, [params.tipoRegistro]);

  const typePractice = useMemo(() => {
    return params.tipoPratica;
  }, [params.tipoPratica]);

  const formContext = useFormikContext<PracticeDetailVM>();

  const values: AccordionInterestedPeopleVM | undefined = useMemo(() => {
    return formContext.values.interestedPeople;
  }, [formContext.values.interestedPeople]);

  const onChange = useEventCallback(
    (
      key: keyof AccordionInterestedPeopleVM,
      value: AccordionInterestedPeopleVM[keyof AccordionInterestedPeopleVM]
    ) => {
      formContext.setFieldValue('interestedPeople', {
        ...(formContext.values.interestedPeople || {}),
        [key]: value,
      });
    }
  );

  const [deleteAlertModal, setDeleteAlertModal] = useState<
    Record<string, boolean | BodyInterestedPeopleVM | undefined>
  >({ open: false, item: undefined });

  const removePersonUnavailable = useHttpClient(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/rimuovi-non-piu-irreperibile-emigrato`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );
  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChange('rowsPerPage', rowsPerPage || 0);
  });

  const onChangeLicense = useEventCallback((value: string, index: number) => {
    const newBody = values?.body?.slice() || [];

    if (newBody.length > 0) {
      newBody[index] = {
        ...(newBody[index] || {}),
        licenseValue: value,
      };
    }

    onChange('body', newBody);
  });

  const onChangeCar = useEventCallback((value: string, index: number) => {
    const newBody = values?.body?.slice() || [];

    if (newBody.length > 0) {
      newBody[index] = {
        ...(newBody[index] || {}),
        carValue: value,
      };
    }

    onChange('body', newBody);
  });

  const onVisibility = useEventCallback((item: BodyInterestedPeopleVM) => {
    const newVisibility = values?.visibilityPersons?.slice() || [];

    if (newVisibility.find((el) => el.idANPR === item.idANPR)) {
      return;
    } else {
      newVisibility.push(item);
    }

    onChange('visibilityPersons', newVisibility);
  });

  const onDelete = useEventCallback((item: BodyInterestedPeopleVM) => {
    let newBody = values?.body?.slice() || [];

    if (!newBody.find((el) => el.idANPR === item.idANPR)) {
      return;
    } else {
      (
        removePersonUnavailable({
          data: item.personItem,
        }) as ResponsePromise<void>
      ).then(() => {
        newBody = newBody.filter((el) => {
          return item.idANPR !== el.idANPR;
        });
        onChange('body', newBody);
        toast.success('Operazione eseguita con successo');
      });
    }
  });

  const onClose = useEventCallback((indexCurr: number) => {
    let newVisibility = values?.visibilityPersons?.slice() || [];

    newVisibility = newVisibility.filter((_, index) => index !== indexCurr);

    onChange('visibilityPersons', newVisibility);
  });

  const saveRestoration = useHttpClient<HttpResponse<{ message: string }>>(
    `${__PRACTICE_API_URL__}/chiusura-pratica/ripristino-pratica`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [restorationModal, setRestorationModal] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const onRestoration = useEventCallback((person?: InterestedPeopleData) => {
    if (!person) {
      return;
    }

    (
      saveRestoration({
        data: {
          codiceEnte: params.codiceEnte,
          tipoRegistro: params.tipoRegistro,
          idAnnoPratica: params.idAnnoPratica,
          progressivoPratica: params.progressivoPratica,
          descrizioneNote: initialValuesNota,
          personeRipristinoList: [person],
        },
      }) as ResponsePromise<HttpResponse<{ message: string }>>
    ).then((response) => {
      const message = response?.data?.payload?.message ?? 'Ripristino completato con successo';
      setRestorationModal({ open: true, message });
    });
  });

  const isWithRestoration: boolean = useMemo(() => {
    return Boolean(
      typeRegister === TypeRegister.Emigration &&
        typePractice === TypePractice.EmigrationN010 &&
        formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.POSITIVO &&
        (formContext.values.practice?.tipoOperazione === '80' ||
          formContext.values.practice?.tipoOperazione === '31') &&
        !formContext.values.practice?.notaOperazioneEmigrazioneN010
    );
    // Not implemented per la 2685
    // ||
    // Boolean(
    //   formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO &&
    //     formContext.values.practice?.tipoOperazione === '90'
    // )
  }, [typeRegister, typePractice, formContext.values.practice]);

  const body: Array<BodyInterestedPeopleVM> = useMemo(() => {
    if (!values?.body) {
      return [];
    }

    return (
      values?.body
        // ?.filter((item) => item.aggregateToPractice)
        .map((item, index) => ({
          ...item,
          license: (
            <ListYesNo
              label=""
              value={item?.licenseValue || ''}
              onChange={(event) => onChangeLicense(event.target.value || '', index)}
              disabled={disabled}
            />
          ),
          car: (
            <ListYesNo
              label=""
              value={item?.carValue || ''}
              onChange={(event) => onChangeCar(event.target.value || '', index)}
              disabled={disabled}
            />
          ),
          action: (
            <div className="pmi-d-flex">
              <IconButton onClick={() => onVisibility(item)}>
                <Visibility />
              </IconButton>
              {(values.body || [])?.length > 1 &&
              item.relationship?.toString() !== '1' &&
              (typeRegister === TypeRegister.Unavailable ||
                typeRegister === TypeRegister.Emigration) ? (
                <IconButton
                  onClick={() => setDeleteAlertModal({ open: true, item })}
                  color="cdmBrand"
                  disabled={disabled || typeRegister === TypeRegister.Emigration}
                >
                  <Delete />
                </IconButton>
              ) : undefined}
              {isWithRestoration && item.personItem?.posizionePersona !== 'R' ? (
                <StepButton
                  isButton
                  variant="outlined"
                  color="cdmBrandThird"
                  onClick={() => onRestoration(item.personItem)}
                  type="button"
                >
                  <Box
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Ripristino
                  </Box>
                </StepButton>
              ) : undefined}
            </div>
          ),
        }))
    );
  }, [values?.body, onDelete, onVisibility, disabled, typeRegister, isWithRestoration]);

  const onChangePerson = useEventCallback(
    (
      key: keyof BodyInterestedPeopleVM,
      value: BodyInterestedPeopleVM[keyof BodyInterestedPeopleVM],
      person?: BodyInterestedPeopleVM
    ) => {
      const filteredPersons = values?.visibilityPersons?.filter(
        (el) => el.idANPR !== person?.idANPR
      );

      if (!filteredPersons) return;

      const updatedPerson: BodyInterestedPeopleVM = {
        ...person,
        [key]: value,
      };

      onChange('visibilityPersons', [...filteredPersons, updatedPerson]);
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {body.length > 0 ? (
          <Table
            ariaLabel="Lista persone interessate dalla pratica"
            page={values?.page || 0}
            rowsPerPage={values?.rowsPerPage}
            body={body}
            count={body?.length || 0}
            head={headCells}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onSort={noop}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          'Nessuna informazione'
        )}
      </Grid>
      <Grid item xs={12}>
        {(values?.visibilityPersons || [])?.map((item, index) => (
          <AccordionPerson
            key={`${item.fiscalCode}-${index}`}
            person={item}
            onChangePerson={onChangePerson}
            onClose={() => {
              onClose(index);
            }}
          />
        ))}
      </Grid>
      <DeleteAlertModal
        title="Cancellazione persona"
        message="Attenzione si sta togliendo una persona dalla pratica. Confermare"
        open={deleteAlertModal.open as boolean}
        onClose={() => setDeleteAlertModal({ open: false, item: undefined })}
        onYes={() => {
          onDelete(deleteAlertModal.item as BodyInterestedPeopleVM);
          setDeleteAlertModal({ open: false, item: undefined });
        }}
        onNo={() => setDeleteAlertModal({ open: false, item: undefined })}
        yesError
      />
      <ErrorModal
        title="Attenzione"
        message={restorationModal.message}
        open={restorationModal.open}
        onClose={() => setRestorationModal({ open: false, message: '' })}
      />
    </Grid>
  );
};

FormComponent.displayName = 'Practice Detail - Form - accordion address - form';

const Form = memo(FormComponent);

export default Form;
